// Digest route: read-only weekly digest view.
// Accessible at /digest/:week

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import { api } from '../lib/api';
import type { SnapshotData } from '../lib/api';
import { ThemeCard } from '../components/ThemeCard';
import { ActionTable } from '../components/ActionTable';
import { formatDate } from '../lib/format';

interface DigestProps {
  lang: Lang;
}

export function Digest({ lang }: DigestProps) {
  const { week } = useParams<{ week: string }>();
  const [snapshot, setSnapshot] = useState<SnapshotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const weekNum = week ? parseInt(week, 10) : null;

  useEffect(() => {
    if (!weekNum) return;
    setLoading(true);
    api.snapshots
      .byWeek(weekNum)
      .then(setSnapshot)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [weekNum]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {t('loading', lang)}
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">
        {error ?? t('noData', lang)}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      {/* Header */}
      <div className="border-b border-neutral-200 pb-6">
        <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2">
          {t('weeklyDigest', lang)}
        </div>
        <h1 className="text-lg font-semibold text-neutral-900">
          {t('weekLabel', lang)} {snapshot.week_number % 100}
        </h1>
        <p className="text-sm text-neutral-500 mt-1">{formatDate(snapshot.iso_date)}</p>
        <p className="text-xs text-neutral-500 mt-3">{t('digestReadOnly', lang)}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card p-4">
          <div className="text-xs text-neutral-500 mb-1">{t('totalMentions', lang)}</div>
          <div className="text-2xl font-semibold tabular-nums">{snapshot.total_mentions ?? 0}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs text-rose-600/70 mb-1">{t('criticalTheme', lang)}</div>
          <div className="text-2xl font-semibold tabular-nums text-rose-600">
            {snapshot.themes.filter((th) => th.status === 'CRITICAL').length}
          </div>
        </div>
      </div>

      {/* Themes */}
      <section>
        <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
          {t('themes', lang)}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {snapshot.themes.map((theme, i) => (
            <ThemeCard key={i} theme={theme} lang={lang} />
          ))}
        </div>
      </section>

      {/* Actions */}
      <section>
        <h2 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3">
          {t('actionRecommendations', lang)}
        </h2>
        <ActionTable actions={snapshot.actions} lang={lang} />
      </section>
    </div>
  );
}
